import { ApiClient } from "../api-client";
import schemaValidators from "./schema-validators";
import { ApiClientError, handleApiErrorWithSchema } from "../errors";
import { Lms } from "../types";

type Answer = {
	answer_text: string;
	answer_weight: number;
};

type Question = {
	question_name: string;
	question_text: string;
	question_type: string;
	points_possible: number;
	position: number;
	answers: Answer[];
};

export default class LmsInfoClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClient["config"]) {
		super(config, schemaValidators);
	}

	getAssessmentUrl = async (courseId: string, quizId: string, type: "view" | "edit") => {
		return handleApiErrorWithSchema(
			super.fetch(
				`${this.config.verityOrigin}/api/verity/lms-course/${courseId}/assessment/${quizId}/link?type=${type}`,
				{ credentials: "include" },
			),
			this.schemaValidators.url,
		);
	};

	getCourseUrl = async (courseId: string, type: "create-assessment") => {
		return handleApiErrorWithSchema(
			super.fetch(`${this.config.verityOrigin}/api/verity/lms-course/${courseId}/link?type=${type}`, {
				credentials: "include",
			}),
			this.schemaValidators.url,
		);
	};

	getInstituteLmsInfo = async () => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/lms`, { credentials: "include" });
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.instituteLmsInfo.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	createMoodleToken = async (accessToken: string) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/lms/moodle-token`, {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ accessToken }),
			});
			if (response.ok) {
				return true;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getTokenExists = async () => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/lms/token`, {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.tokenExists.validate(obj)) {
					return obj.exists;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	deleteToken = async () => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/lms/token`, {
				method: "DELETE",
				credentials: "include",
			});
			if (response.ok) {
				return true;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getLTIRef = async (instituteId: number) => {
		try {
			const resp = await super.fetch(new URL(`/api/verity/ltiRef/${instituteId}`, `${this.config.verityOrigin}`), {
				credentials: "include",
			});
			if (resp.ok) {
				const data: unknown = await resp.text();
				if (typeof data === "string") {
					return data;
				}
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
	};

	getLTIRefByHostname = async (hostname: string) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/verity/ltiRef/hostname/${hostname}`, `${this.config.verityOrigin}`),
				{
					credentials: "include",
				},
			);
			if (resp.ok) {
				const data: unknown = await resp.text();
				if (typeof data === "string") {
					return data;
				}
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
	};

	getSessionToken = async (lmsCourseId: string) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/lms/session-token?lmsCourseId=${lmsCourseId}`,
				{
					credentials: "include",
				},
			);
			if (response.ok) {
				const token = ((await response.json()) as { sessionToken: string }).sessionToken;
				return token;
			}
		} catch (error) {
			console.error(error);
		}
	};

	testToken = async ({
		lms,
		category,
		lmsProvidedCourseId,
		accessToken,
	}: {
		lms: string;
		category: string;
		lmsProvidedCourseId?: string | null;
		accessToken?: string | null;
	}) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/scope/${category}`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					lms,
					lmsProvidedCourseId,
					accessToken,
				}),
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.tokenTestResults.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	createAssessment = async (lms: Lms, lmsCourseId: string) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/lms/${lms}/lms-course/${lmsCourseId}/lms-assessment`,
				{
					method: "POST",
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				return obj;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	updateAssessment = async (lms: Lms, lmsCourseId: string, lmsAssessmentId: string, accessCode: string) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/lms-course/${lmsCourseId}/lms-assessment/${lmsAssessmentId}`,
				{
					method: "PUT",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ lms, accessCode }),
					credentials: "include",
				},
			);
			return response.ok;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	addQuestionToAssessment = async (
		lms: Lms,
		lmsCourseId: string,
		lmsAssessmentId: string,
		origin: string,
		question: Question,
	) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/lms-course/${lmsCourseId}/lms-assessment/${lmsAssessmentId}/question`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ lms, origin, question }),
					credentials: "include",
				},
			);
			return response.ok;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	deleteAssessment = async (assessmentId: number, deleteFromLms?: boolean) => {
		try {
			const url = new URL(`${this.config.verityOrigin}/api/verity/assessment/${assessmentId}`);
			if (deleteFromLms) {
				url.searchParams.set("deleteFromLms", "true");
			}
			const response = await super.fetch(url, {
				method: "DELETE",
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.deleteResponse.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getAllAssessmentInfoById = async (assessmentId: number) => {
		try {
			const resp = await super.fetch(new URL(`/api/verity/assessment/${assessmentId}`, `${this.config.verityOrigin}`), {
				credentials: "include",
			});
			if (resp.ok) {
				const obj: unknown = await resp.json();
				if (this.schemaValidators.deleteAssessmentInfo.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getAssessmentInfoById = async (assessmentId: number) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/verity/assessment/${assessmentId}/info`, `${this.config.verityOrigin}`),
				{
					credentials: "include",
				},
			);
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.assessmentInfoAlternate.validate(data)) {
					return {
						...data,
						courseId: data.lmsProvidedCourseId,
						quizId: data.lmsProvidedAssessmentId,
					};
				}
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		throw new ApiClientError();
	};
}
